// ─── delaiDeGarde.js ─────────────────────────────────────────────────
// 2026-08-21 — une attente sans fin est un bug, même quand tout le reste
// est correct.
//
// L'écran de publication a tourné dans le vide pendant deux jours parce
// qu'un `fetch` sans délai de garde ne rendait jamais la main : pas
// d'erreur, pas d'issue, juste un rond qui tourne. Le balayage qui a suivi
// a trouvé la même forme ailleurs, dont un repli d'image qui ne se résout
// que sur `onload` ou `onerror` — et donc jamais si la requête reste
// suspendue.
//
// Ces deux aides bornent l'attente. Elles ne rendent rien plus fiable :
// elles rendent l'échec visible, ce qui est le minimum.

/** Délai par défaut : généreux, pour ne pas couper un traitement lent. */
export const DELAI_PAR_DEFAUT_MS = 20000;

/**
 * `fetch` qui abandonne au lieu de rester suspendu.
 *
 * L'abandon est signalé comme une erreur nommée, jamais confondu avec une
 * panne réseau : les deux ne se corrigent pas de la même façon.
 *
 * @throws {Error} `name === 'DelaiDeGardeDepasse'` en cas d'abandon.
 */
export async function fetchAvecDelai(url, options = {}, {
  timeoutMs = DELAI_PAR_DEFAUT_MS,
  fetchApi = globalThis.fetch,
} = {}) {
  const controleur = typeof AbortController === 'function' ? new AbortController() : null;
  const delai = (controleur && Number.isFinite(timeoutMs) && timeoutMs > 0)
    ? setTimeout(() => controleur.abort(), timeoutMs)
    : null;
  try {
    return await fetchApi(url, {
      ...options,
      ...(controleur ? { signal: controleur.signal } : {}),
    });
  } catch (err) {
    if (controleur?.signal?.aborted) {
      const abandon = new Error(`Pas de réponse en ${Math.round(timeoutMs / 1000)} s.`);
      abandon.name = 'DelaiDeGardeDepasse';
      throw abandon;
    }
    throw err;
  } finally {
    if (delai) clearTimeout(delai);
  }
}

/**
 * Borne une promesse qui pourrait ne jamais se résoudre.
 *
 * Rend `valeurSiDepasse` plutôt que de lever : les appelants visés ont déjà
 * un comportement de repli (rendre `null` et continuer sans l'image), et une
 * exception les ferait échouer là où ils savaient se passer du résultat.
 */
export function promesseAvecDelai(promesse, {
  timeoutMs = DELAI_PAR_DEFAUT_MS,
  valeurSiDepasse = null,
} = {}) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return promesse;
  let delai = null;
  const garde = new Promise((resolve) => {
    delai = setTimeout(() => resolve(valeurSiDepasse), timeoutMs);
  });
  return Promise.race([promesse, garde]).finally(() => {
    if (delai) clearTimeout(delai);
  });
}
